import { useCallback, useEffect, useState } from "react";
import { UserCircle, Crown, MapTrifold, PenNib, Scales, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { ROLE_LABELS, JOB_STATUS_LABELS } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";

const ICON = { owner: Crown, field_executive: MapTrifold, report_writer: PenNib };
const OPEN_STATUSES = ["enquiry", "job_assigned", "docs_collected", "field_team", "report_writer", "draft_data_in", "draft_prepared", "admin_review", "sent_to_client", "client_approved", "invoiced"];
const today = () => new Date().toISOString().slice(0, 10);

export default function Workload() {
  const [team, setTeam] = useState(null);
  const [jobs, setJobs] = useState([]);

  const load = useCallback(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => setTeam([]));
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!team) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading workload…</div>;

  const jobsFor = (id) => jobs.filter((j) => j.field_executive_id === id || j.report_writer_id === id);
  const rows = team.map((m) => {
    const mine = jobsFor(m.id);
    const open = mine.filter((j) => OPEN_STATUSES.includes(j.status));
    const overdue = open.filter((j) => j.deadline && j.deadline.slice(0, 10) < today());
    const dueToday = open.filter((j) => j.deadline && j.deadline.slice(0, 10) === today());
    const byStage = OPEN_STATUSES
      .map((s) => ({ s, n: open.filter((j) => j.status === s).length }))
      .filter((x) => x.n > 0);
    return { ...m, total: mine.length, open: open.length, overdue: overdue.length, dueToday: dueToday.length, byStage };
  }).sort((a, b) => a.open - b.open || a.name.localeCompare(b.name));

  const unassigned = jobs.filter((j) => !j.field_executive_id && !j.report_writer_id && OPEN_STATUSES.includes(j.status));
  const maxOpen = Math.max(1, ...rows.map((r) => r.open));
  const lightest = rows.filter((r) => r.is_active !== false)[0];

  return (
    <div className="space-y-6" data-testid="workload-page">
      <PageHeader overline="Team" title="Workload" subtitle="Who is carrying how much open work right now — assign the next job fairly" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Open jobs" value={rows.reduce((s, r) => s + r.open, 0)} testid="stat-wl-open" />
        <Stat label="Overdue" value={rows.reduce((s, r) => s + r.overdue, 0)} tone="text-destructive" testid="stat-wl-overdue" />
        <Stat label="Unassigned" value={unassigned.length} tone="text-amber-600" sub="waiting for a person" testid="stat-wl-unassigned" />
        <Stat label="Lightest load" value={lightest ? lightest.name.split(" ")[0] : "—"} tone="text-emerald-600" sub={lightest ? `${lightest.open} open jobs` : ""} testid="stat-wl-lightest" />
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={UserCircle}>No team members yet. Add staff under Team → All Team Members.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((m, i) => {
            const Icon = ICON[m.role] || UserCircle;
            return (
              <Card key={m.id} className={`p-5 border-border ${m.is_active === false ? "opacity-60" : ""}`} data-testid={`workload-${m.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-primary/5 flex items-center justify-center shrink-0"><Icon size={22} weight="duotone" className="text-primary" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-primary truncate">{m.name}</div>
                    <div className="text-xs text-accent">{ROLE_LABELS[m.role] || m.role}</div>
                  </div>
                  {i === 0 && m.is_active !== false && (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1 inline-flex items-center gap-1" data-testid={`workload-next-${m.id}`}>
                      <Scales size={11} /> Assign next
                    </span>
                  )}
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Open work</span>
                    <span className="tabular font-semibold text-primary" data-testid={`workload-open-${m.id}`}>{m.open} of {m.total} total</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-accent transition-[width] duration-500" style={{ width: `${(m.open / maxOpen) * 100}%` }} />
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-3">
                  {m.byStage.length === 0 && <span className="text-xs text-muted-foreground">Nothing open — free for new work.</span>}
                  {m.byStage.map((x) => (
                    <span key={x.s} className="text-[11px] rounded-full border border-border bg-muted px-2 py-0.5 text-primary">
                      {JOB_STATUS_LABELS[x.s]} · {x.n}
                    </span>
                  ))}
                </div>

                {(m.overdue > 0 || m.dueToday > 0) && (
                  <div className="mt-3 flex flex-wrap gap-3 text-xs" data-testid={`workload-alerts-${m.id}`}>
                    {m.overdue > 0 && <span className="text-destructive font-medium inline-flex items-center gap-1"><WarningCircle size={13} weight="fill" /> {m.overdue} overdue</span>}
                    {m.dueToday > 0 && <span className="text-amber-600 font-medium">{m.dueToday} due today</span>}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
